import { ExternalLink, History, Loader2 } from 'lucide-react'
import { Link } from 'react-router-dom'
import { useCitAccrualHistory } from '../../../hooks/useCitAccruals'
import { useCurrency } from '../../../hooks/useCurrency'
import { displayDate } from '../../../utils/dates'

const MONTH_NAMES_SHORT = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
]

const SIZE_LABEL: Record<'small' | 'medium' | 'large', string> = {
  small: 'Small',
  medium: 'Medium',
  large: 'Large',
}

const SIZE_CHIP: Record<'small' | 'medium' | 'large', string> = {
  small:  'bg-sage-50 text-sage-700 border-sage-200',
  medium: 'bg-amber-50 text-amber-700 border-amber-200',
  large:  'bg-brand-50 text-brand-700 border-brand-200',
}

function formatPeriod(periodEnd: string): string {
  const [y, m] = periodEnd.split('-').map(s => parseInt(s, 10))
  if (!Number.isFinite(y) || !Number.isFinite(m)) return periodEnd
  return `${MONTH_NAMES_SHORT[m - 1]} ${y}`
}

export function CitHistoryTable() {
  const { format } = useCurrency()
  const { data: history, isLoading } = useCitAccrualHistory()

  return (
    <section className="max-w-5xl mx-auto space-y-3">
      <div className="flex items-center gap-2 px-1">
        <History className="h-4 w-4 text-slate-500" />
        <h2 className="text-sm font-semibold text-slate-700">Recent CIT accruals</h2>
      </div>

      {isLoading ? (
        <div className="bg-white rounded-2xl border border-slate-200 p-8 text-center">
          <Loader2 className="h-5 w-5 animate-spin text-slate-400 mx-auto" />
        </div>
      ) : (history ?? []).length === 0 ? (
        <div className="bg-white rounded-2xl border border-slate-200 p-8 text-center">
          <p className="text-sm text-slate-500">
            No CIT accruals yet. Pick a period above to post your first one.
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-50 text-[11px] uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-3 py-2 text-left font-medium">Period</th>
                  <th className="px-3 py-2 text-right font-medium">YTD Profit</th>
                  <th className="px-3 py-2 text-left font-medium">Size</th>
                  <th className="px-3 py-2 text-right font-medium">CIT %</th>
                  <th className="px-3 py-2 text-right font-medium">This Period</th>
                  <th className="px-3 py-2 text-right font-medium">YTD Total</th>
                  <th className="px-3 py-2 text-left font-medium">Journal</th>
                  <th className="px-3 py-2 text-left font-medium">Posted</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {(history ?? []).map(h => (
                  <tr key={h.id}>
                    <td className="px-3 py-2 whitespace-nowrap text-slate-800 font-medium">
                      {formatPeriod(h.periodEndDate)}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-slate-700 whitespace-nowrap">
                      {format(h.ytdProfitBeforeTax)}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium ${SIZE_CHIP[h.companySize]}`}>
                        {SIZE_LABEL[h.companySize]}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-slate-700 whitespace-nowrap">
                      {h.citRatePercent.toFixed(2)}%
                    </td>
                    <td className={`px-3 py-2 text-right tabular-nums whitespace-nowrap font-medium ${
                      h.periodCharge < 0 ? 'text-amber-700' : 'text-slate-900'
                    }`}>
                      {format(h.periodCharge)}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums font-medium text-gray-900 whitespace-nowrap">
                      {format(h.totalTaxCharge)}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      {h.journalEntryNumber ? (
                        <Link
                          to={`/accounting/journal?search=${encodeURIComponent(h.journalEntryNumber)}`}
                          className="inline-flex items-center gap-1 text-sm font-medium text-brand-600 hover:text-brand-700 font-mono tabular-nums"
                        >
                          {h.journalEntryNumber}
                          <ExternalLink className="h-3 w-3" />
                        </Link>
                      ) : (
                        <span className="text-slate-400">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-sm text-slate-500 whitespace-nowrap">
                      {displayDate(h.postedAt)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </section>
  )
}
